import type { UserSex } from "@arbeidsark/shared"
import { useCallback, useMemo, useRef } from "react"
import styled from "styled-components"
import { FormField, Input, Label } from "~/components"
import NumberInput from "~/components/Input/NumberInput"
import SelectSex from "~/components/Select/SelectSex"
import { theme } from "~/theme"
import { useClickOutside, useForm, useTranslate } from "~/ur-react-hooks"
import { useToast, useUser } from "~/utils"
import { useErrorHandling } from "~/utils/error"
import { useOrganizationMembership } from "~/utils/hooks/useOrganizationMembership"
import { trpc } from "~/utils/trpc"
import GradeSelect from "../worksheets/components/selects/GradeSelect"

const Layout = styled.div`
  position: relative;
`

const FormTitle = styled.h4`
  margin-top: 0;
  margin-bottom: 1rem;
`

const FormSection = styled.div<{ first: boolean }>`
  margin-top: ${(props) => (props.first ? "1rem" : "3rem")};
`

export type SettingsInfoProps = {}

interface SettingsInfoForm {
  workPlace: string | null
  schoolGrade: number | null

  firstName: string | null
  lastName: string | null
  age: number | null
  sex: UserSex | null
}

const SettingsInfo: React.FC<SettingsInfoProps> = () => {
  const translations = useTranslate({
    workPlace: "settings.work-place",
    whichClassAreyouTeaching: "settings.which-class-are-you-teaching",
    school: "common.school",
    schoolGrade: "common.school-grade",
    firstName: "common.first-name",
    lastName: "common.last-name",
    age: "common.age",
    sex: "common.sex",
    email: "common.email",
    personalInformation: "settings.personal-information",
    savedSuccesfully: "settings.saved-successfully",
  })

  const organization = useOrganizationMembership()
  const partOfOrganizationWithName = organization?.organization.name

  const trpcContext = trpc.useContext()
  const me = useUser()
  const toast = useToast()

  const { getToastInfo } = useErrorHandling()

  const { mutate: updateUser } = trpc.users.updateUser.useMutation({
    onSuccess() {
      toast("success", translations.savedSuccesfully)
      trpcContext.users.me.invalidate()
    },
    onError(error) {
      const toastInfo = getToastInfo(error.message)
      toast(toastInfo.type, toastInfo.message)
    },
  })

  const initialValues = useMemo(
    () => ({
      workPlace: me.workPlace,
      schoolGrade: me.schoolGrade,
      firstName: me.firstName ?? "",
      lastName: me.lastName ?? "",
      age: me.age,
      sex: me.sex,
    }),
    [me],
  )

  const {
    formValues,
    formChangeHandler,
    formErrors,
    formEdited,
    updateInitialValues,
    updateForm,
    resetForm,
  } = useForm<SettingsInfoForm>({
    values: initialValues,
  })

  const save = useCallback(() => {
    if (!formEdited) {
      return
    }

    updateUser({
      id: me.id,
      user: {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        workPlace: formValues.workPlace ?? undefined,
        schoolGrade: formValues.schoolGrade ?? undefined,
        sex: formValues.sex ?? undefined,
        age: formValues.age ?? undefined,
      },
    })

    updateInitialValues({
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      workPlace: formValues.workPlace,
      schoolGrade: formValues.schoolGrade,
      sex: formValues.sex,
      age: formValues.age,
    })

    resetForm()
  }, [formValues, formEdited, updateUser, updateInitialValues, resetForm, me])

  const layoutRef = useRef<HTMLDivElement>(null)

  useClickOutside(layoutRef, () => {
    save()
  })

  return (
    <Layout ref={layoutRef}>
      <FormSection first={true}>
        <FormTitle>{translations.workPlace}</FormTitle>
        {partOfOrganizationWithName !== undefined ? (
          <FormField>
            <Label>{translations.school}</Label>
            <Input
              width="100%"
              value={partOfOrganizationWithName}
              onChange={() => {}}
              disabled={true}
              iconRightProps={{
                icon: "lock-keyhole",
                type: "solid",
              }}
            />
          </FormField>
        ) : (
          <FormField>
            <Label>{translations.school}</Label>
            <Input
              type="text"
              name="school"
              width="100%"
              value={formValues.workPlace ?? ""}
              onChange={formChangeHandler("workPlace")}
            />
          </FormField>
        )}
        <FormField>
          <Label>{translations.schoolGrade}</Label>
          <GradeSelect
            value={formValues.schoolGrade}
            placeholder={translations.whichClassAreyouTeaching}
            placeholderColor={theme.colors.gray60}
            clearableValue={null}
            onChange={(value) => {
              updateForm({
                schoolGrade: value,
              })
            }}
          />
        </FormField>
      </FormSection>
      <FormSection first={false}>
        <FormTitle>{translations.personalInformation}</FormTitle>
        <FormField>
          <Label>{translations.firstName}</Label>
          <Input
            type="text"
            name="firstName"
            width="100%"
            value={formValues.firstName ?? ""}
            onChange={formChangeHandler("firstName")}
            error={formErrors.firstName}
          />
        </FormField>
        <FormField>
          <Label>{translations.lastName}</Label>
          <Input
            type="text"
            name="lastName"
            width="100%"
            value={formValues.lastName ?? ""}
            onChange={formChangeHandler("lastName")}
            error={formErrors.lastName}
          />
        </FormField>
        <FormField>
          <Label>{translations.age}</Label>
          <NumberInput
            width="100%"
            value={formValues.age}
            onChange={formChangeHandler("age")}
            error={formErrors.age}
          />
        </FormField>
        <FormField>
          <Label>{translations.sex}</Label>
          <SelectSex
            value={formValues.sex ?? null}
            onChange={formChangeHandler("sex")}
            placeholderColor={theme.colors.gray60}
            clearableValue={null}
          />
        </FormField>
        <FormField>
          <Label>{translations.email}</Label>
          <Input
            width="100%"
            value={me.email}
            onChange={() => {}}
            error={formErrors.age}
            disabled={true}
            iconRightProps={{
              icon: "lock-keyhole",
              type: "solid",
            }}
          />
        </FormField>
      </FormSection>
    </Layout>
  )
}

export default SettingsInfo
